import { Controller } from "@hotwired/stimulus"

const OPERATORS = {
  text: [
    { value: "cont",    label: "contém" },
    { value: "eq",      label: "igual a" },
    { value: "start",   label: "começa com" },
    { value: "blank",   label: "em branco",  noValue: true },
    { value: "present", label: "preenchido", noValue: true },
  ],
  select: [
    { value: "eq",      label: "igual a" },
    { value: "blank",   label: "em branco",  noValue: true },
    { value: "present", label: "preenchido", noValue: true },
  ],
  number: [
    { value: "eq",   label: "= igual a" },
    { value: "gteq", label: ">= maior ou igual" },
    { value: "lteq", label: "<= menor ou igual" },
    { value: "gt",   label: "> maior que" },
    { value: "lt",   label: "< menor que" },
  ],
  date: [
    { value: "gteq", label: "a partir de" },
    { value: "lteq", label: "até" },
    { value: "eq",   label: "igual a" },
    { value: "blank",   label: "em branco",  noValue: true },
    { value: "present", label: "preenchido", noValue: true },
  ],
}

export default class extends Controller {
  static targets = ["rows", "badge"]
  static values  = { groups: Array, tableId: String }

  connect() {
    this.restoreState()
  }

  // ── All fields flat (for lookup) ───────────────────────
  get allFields() {
    return this.groupsValue.flatMap(g => g.fields)
  }

  // ── State ──────────────────────────────────────────────
  get stateKey() {
    return `datatable:${this.tableIdValue}:state`
  }

  savedFilters() {
    try {
      return JSON.parse(localStorage.getItem(this.stateKey) || "{}").filters || {}
    } catch { return {} }
  }

  restoreState() {
    const filters = this.savedFilters()
    const entries = Object.entries(filters)

    if (entries.length === 0) return

    entries.forEach(([key, value]) => {
      const i     = key.lastIndexOf("_")
      const field = key.slice(0, i)
      const op    = key.slice(i + 1)
      this.addRow({ field, op, value })
    })

    this.refreshBadge(filters)
  }

  // ── Row management ─────────────────────────────────────
  addRow({ field = "", op = "", value = "" } = {}) {
    const row = document.createElement("div")
    row.className = "row g-2 mb-2 align-items-center filter-row"
    row.innerHTML = this.buildRowHTML(field)
    this.rowsTarget.appendChild(row)

    if (field) {
      const optEl = row.querySelector(`.field-select option[value="${field}"]`)
      const type  = optEl?.dataset.fieldType || "text"
      const opts  = JSON.parse(optEl?.dataset.options || "[]")
      this.populateOperators(row, type, op)
      this.renderValueInput(row, type, opts, value)
    }
  }

  removeRow(event) {
    event.target.closest(".filter-row").remove()
  }

  buildRowHTML(selectedField = "") {
    const optgroups = this.groupsValue.map(group => {
      const options = group.fields.map(f => {
        const sel = f.name === selectedField ? "selected" : ""
        return `<option value="${f.name}" ${sel}
                  data-field-type="${f.type || "text"}"
                  data-options='${JSON.stringify(f.options || [])}'>
                  ${f.label}
                </option>`
      }).join("")
      return group.group
        ? `<optgroup label="${group.group}">${options}</optgroup>`
        : options
    }).join("")

    return `
      <div class="col-md-4">
        <select class="form-select form-select-sm field-select"
                data-action="change->datatable-filter-builder#onFieldChange">
          <option value="">Campo...</option>
          ${optgroups}
        </select>
      </div>
      <div class="col-md-3">
        <select class="form-select form-select-sm op-select">
          <option value="">─</option>
        </select>
      </div>
      <div class="col-md-4">
        <div class="val-wrapper">
          <input type="text" class="form-control form-control-sm" placeholder="valor..." disabled>
        </div>
      </div>
      <div class="col-md-1 text-center">
        <button type="button"
                class="btn btn-sm btn-outline-danger"
                data-action="click->datatable-filter-builder#removeRow"
                title="Remover">
          <i class="fa fa-times"></i>
        </button>
      </div>`
  }

  // ── Field change ───────────────────────────────────────
  onFieldChange(event) {
    const row   = event.target.closest(".filter-row")
    const optEl = event.target.selectedOptions[0]
    const type  = optEl?.dataset.fieldType || "text"
    const opts  = JSON.parse(optEl?.dataset.options || "[]")
    this.populateOperators(row, type)
    this.renderValueInput(row, type, opts)
  }

  populateOperators(row, type, selectedOp = "") {
    const select = row.querySelector(".op-select")
    const ops    = OPERATORS[type] || OPERATORS.text

    select.innerHTML = ops.map(op => `
      <option value="${op.value}"
              data-no-value="${!!op.noValue}"
              ${op.value === selectedOp ? "selected" : ""}>
        ${op.label}
      </option>`).join("")

    select.addEventListener("change", () => this.toggleValueInput(row, select))
    this.toggleValueInput(row, select)
  }

  toggleValueInput(row, select) {
    const noValue = select.selectedOptions[0]?.dataset.noValue === "true"
    row.querySelector(".val-wrapper").style.display = noValue ? "none" : ""
  }

  renderValueInput(row, type, options = [], prefill = "") {
    const wrapper = row.querySelector(".val-wrapper")

    if ((type === "select" || type === "boolean") && options.length) {
      wrapper.innerHTML = `
        <select class="form-select form-select-sm val-input">
          <option value=""></option>
          ${options.map(([label, val]) =>
            `<option value="${val}" ${String(val) === String(prefill) ? "selected" : ""}>${label}</option>`
          ).join("")}
        </select>`
    } else if (type === "date") {
      wrapper.innerHTML = `<input type="date" class="form-control form-control-sm val-input" value="${prefill}">`
    } else if (type === "number") {
      wrapper.innerHTML = `<input type="number" class="form-control form-control-sm val-input" value="${prefill}">`
    } else {
      wrapper.innerHTML = `<input type="text" class="form-control form-control-sm val-input" value="${prefill}" placeholder="valor...">`
    }
  }

  // ── Serialize ──────────────────────────────────────────
  serialize() {
    const filters = {}

    this.rowsTarget.querySelectorAll(".filter-row").forEach(row => {
      const field   = row.querySelector(".field-select")?.value
      const opSel   = row.querySelector(".op-select")
      const op      = opSel?.value
      const noValue = opSel?.selectedOptions[0]?.dataset.noValue === "true"
      const val     = noValue ? "true" : (row.querySelector(".val-input")?.value || "")

      if (!field || !op) return
      if (!noValue && !val) return

      const key = `${field}_${op}`
      filters[key] = val
    })

    return filters
  }

  // ── Apply / Clear ──────────────────────────────────────
  apply(event) {
    event?.preventDefault()
    const filters = this.serialize()

    const stored = JSON.parse(localStorage.getItem(this.stateKey) || "{}")
    stored.filters = filters
    localStorage.setItem(this.stateKey, JSON.stringify(stored))

    window.dispatchEvent(new CustomEvent("filters:apply", { detail: filters }))
    this.refreshBadge(filters)
    this.closeModal()
  }

  clear(event) {
    event?.preventDefault()
    this.rowsTarget.innerHTML = ""

    const stored = JSON.parse(localStorage.getItem(this.stateKey) || "{}")
    stored.filters = {}
    localStorage.setItem(this.stateKey, JSON.stringify(stored))

    window.dispatchEvent(new CustomEvent("filters:apply", { detail: {} }))
    window.dispatchEvent(new CustomEvent("datatable:clear-search", { detail: { tableId: this.tableIdValue } }))
    this.refreshBadge({})
    this.closeModal()
  }

  refreshBadge(filters) {
    const count = Object.keys(filters).length

    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = count
      this.badgeTarget.classList.toggle("d-none", count === 0)
    }

    const card = this.element.closest(".card") ||
                 document.getElementById(this.tableIdValue)?.closest(".card")
    const gemBadge = card?.querySelector("[data-filter-count]")
    if (gemBadge) {
      gemBadge.textContent = count
      gemBadge.classList.toggle("d-none", count === 0)
    }
  }

  closeModal() {
    const modalEl = this.element.querySelector(".modal")
    if (modalEl) bootstrap.Modal.getInstance(modalEl)?.hide()
  }
}
